import { format } from "date-fns";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  AlertTriangle,
  Building2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  useClient,
  useClientAddresses,
  useClientContacts,
  useClientReminders,
} from "@/features/clients/api";
import {
  CLIENT_TYPES,
  CONTACT_TAGS,
  DELIVERY_TERMS,
  FREIGHT_TERMS,
  GST_TREATMENTS,
  PACKING_TYPES,
  PAYMENT_TERMS,
  PRICE_LISTS,
  RATE_BASIS,
  formatMoney,
  labelFor,
} from "@/features/clients/constants";
import { useClientId } from "@/features/clients/detail/use-client-id";
import { cn } from "@/lib/utils";

export function ClientProfilePage() {
  const clientId = useClientId();
  const { data: client } = useClient(clientId);
  const { data: contacts = [] } = useClientContacts(clientId);
  const { data: addresses = [] } = useClientAddresses(clientId);
  const { data: reminders = [] } = useClientReminders(clientId);

  if (!client) return null;

  const primary = contacts.find((c) => c.is_primary) ?? contacts[0];
  const openReminders = reminders.filter((r) => !r.is_done);
  const gaps: string[] = [];
  if (!contacts.length) gaps.push("No contact person recorded");
  if (!addresses.some((a) => a.address_type === "billing")) gaps.push("No billing address on file");
  if (
    (client.gst_treatment === "registered" || client.gst_treatment === "composition") &&
    !client.gst_no
  ) {
    gaps.push("GSTIN missing for a registered client");
  }

  return (
    <div className="p-4 md:p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Profile</h2>
          <p className="text-2xs text-muted-foreground">
            Company master data used across sales, dispatch and invoices.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/clients/$clientId/edit" params={{ clientId }}>
            <Pencil />
            Edit profile
          </Link>
        </Button>
      </div>

      {gaps.length ? (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-warning/35 bg-warning/8 px-3 py-2">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
          <ul className="text-xs text-muted-foreground">
            {gaps.map((g) => (
              <li key={g}>• {g}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <DetailSection title="Company information" icon={<Building2 />}>
          <DetailRow label="Legal name" value={client.name} />
          <DetailRow label="Client code" value={client.code} mono />
          <DetailRow label="Type" value={labelFor(CLIENT_TYPES, client.client_type)} />
          <DetailRow label="Industry" value={client.industry_segment} />
          <DetailRow label="Lead source" value={client.lead_source} />
          <DetailRow
            label="Onboarded"
            value={
              client.onboarded_on ? format(new Date(client.onboarded_on), "dd MMM yyyy") : null
            }
          />
          <DetailRow
            label="Phone"
            value={
              client.work_phone ? (
                <a href={`tel:${client.work_phone}`} className="text-primary hover:underline">
                  {client.work_phone}
                </a>
              ) : null
            }
          />
          <DetailRow
            label="Email"
            value={
              client.email ? (
                <a href={`mailto:${client.email}`} className="text-primary hover:underline">
                  {client.email}
                </a>
              ) : null
            }
          />
          <DetailRow
            label="Website"
            value={
              client.website ? (
                <a
                  href={client.website.startsWith("http") ? client.website : `https://${client.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {client.website}
                </a>
              ) : null
            }
          />
          <DetailRow
            label="Tags"
            value={
              client.groups.length ? (
                <span className="flex flex-wrap gap-1">
                  {client.groups.map((g) => (
                    <Badge key={g} variant="secondary">
                      {g}
                    </Badge>
                  ))}
                </span>
              ) : null
            }
          />
          <DetailRow
            label="Registered address"
            value={
              <span className="leading-relaxed">
                {client.addr1}
                {client.addr2 ? `, ${client.addr2}` : ""}
                <br />
                {client.city}, {client.state} — {client.pincode}
              </span>
            }
          />
        </DetailSection>

        <DetailSection title="Commercial & tax" icon={<ShieldCheck />}>
          <DetailRow label="GST treatment" value={labelFor(GST_TREATMENTS, client.gst_treatment)} />
          <DetailRow label="GSTIN" value={client.gst_no} mono />
          <DetailRow label="Place of supply" value={client.place_of_supply || client.state} />
          <DetailRow label="PAN" value={client.pan_no} mono />
          <DetailRow label="CIN" value={client.cin_no} mono />
          <DetailRow label="MSME / Udyam" value={client.msme_udyam_no} mono />
          <DetailRow label="TDS" value={client.tds_applicable ? "Applicable" : "Not applicable"} />
          <DetailRow label="TCS" value={client.tcs_applicable ? "Applicable" : "Not applicable"} />
          <Separator className="my-1" />
          <DetailRow label="Payment terms" value={labelFor(PAYMENT_TERMS, client.payment_terms)} />
          <DetailRow
            label="Credit days"
            value={client.credit_days ? String(client.credit_days) : null}
          />
          <DetailRow
            label="Credit limit"
            value={client.credit_limit > 0 ? formatMoney(client.credit_limit) : "No limit"}
          />
          <DetailRow
            label="Opening balance"
            value={
              client.opening_balance !== 0 || client.opening_balance_as_of ? (
                <span className="tabular">
                  {formatMoney(client.opening_balance)}
                  {client.opening_balance_as_of
                    ? ` as of ${format(new Date(client.opening_balance_as_of), "dd MMM yyyy")}`
                    : ""}
                </span>
              ) : (
                "Nil"
              )
            }
          />
          <DetailRow
            label="To collect"
            value={
              client.outstanding_balance > 0 ? (
                <span className="font-medium tabular">{formatMoney(client.outstanding_balance)}</span>
              ) : (
                "Nil"
              )
            }
          />
          <DetailRow
            label="Overdue"
            value={
              client.overdue_balance > 0 ? (
                <span className="font-medium tabular text-destructive">
                  {formatMoney(client.overdue_balance)}
                </span>
              ) : (
                "Nil"
              )
            }
          />
          <DetailRow label="Price list" value={labelFor(PRICE_LISTS, client.price_list)} />
          <DetailRow label="Rate basis" value={labelFor(RATE_BASIS, client.rate_basis)} />
          <DetailRow label="Currency" value={client.currency} />
        </DetailSection>

        <DetailSection title="Dispatch & quality" icon={<MapPin />}>
          <DetailRow label="Delivery terms" value={labelFor(DELIVERY_TERMS, client.delivery_terms)} />
          <DetailRow label="Freight" value={labelFor(FREIGHT_TERMS, client.freight_terms)} />
          <DetailRow label="Packing" value={labelFor(PACKING_TYPES, client.packing_type)} />
          <DetailRow label="Transporter" value={client.preferred_transporter} />
          <DetailRow label="Quality standard" value={client.quality_standard} />
          <DetailRow
            label="Test certificate"
            value={client.requires_test_certificate ? "Required with dispatch" : "Not required"}
          />
          <DetailRow
            label="Third-party inspection"
            value={client.requires_third_party_inspection ? "Required" : "Not required"}
          />
          <DetailRow label="Dispatch instructions" value={client.dispatch_instructions} />
          <DetailRow label="Tolerance notes" value={client.tolerance_notes} />
          <DetailRow label="Internal notes" value={client.notes} />
        </DetailSection>

        <div className="space-y-4">
          <DetailSection
            title="Primary contact"
            icon={<Users />}
            action={
              <Button asChild variant="ghost" size="xs">
                <Link to="/clients/$clientId/contacts" params={{ clientId }}>
                  View all
                </Link>
              </Button>
            }
          >
            {primary ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2.5">
                  <AvatarInitials name={primary.name} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{primary.name}</p>
                    <p className="truncate text-2xs text-muted-foreground">
                      {[primary.designation, primary.department].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                </div>
                {primary.mobile ? (
                  <a
                    href={`tel:${primary.mobile}`}
                    className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                  >
                    <Phone className="size-3" />
                    {primary.mobile}
                  </a>
                ) : null}
                {primary.email ? (
                  <a
                    href={`mailto:${primary.email}`}
                    className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                  >
                    <Mail className="size-3" />
                    {primary.email}
                  </a>
                ) : null}
                {primary.tags.length ? (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {primary.tags.map((t) => (
                      <Badge key={t} variant="secondary">
                        {labelFor(CONTACT_TAGS, t)}
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No contacts yet.</p>
            )}
          </DetailSection>

          {openReminders.length ? (
            <DetailSection title={`Open reminders (${openReminders.length})`} icon={<AlertTriangle />}>
              <ul className="space-y-1.5">
                {openReminders.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex justify-between gap-2 text-xs">
                    <span className="truncate">{r.title}</span>
                    <span className="shrink-0 text-2xs text-muted-foreground tabular">
                      {r.due_at ? format(new Date(r.due_at), "dd MMM") : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </DetailSection>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DetailSection({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold">
          {icon ? <span className="text-muted-foreground [&_svg]:size-3.5">{icon}</span> : null}
          {title}
        </h3>
        {action}
      </div>
      <dl className="space-y-0">{children}</dl>
    </section>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value?: ReactNode;
  mono?: boolean;
}) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 border-b border-border/60 py-1.5 last:border-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("min-w-0 text-xs", mono && "font-mono", empty && "text-muted-foreground")}>
        {empty ? "—" : value}
      </dd>
    </div>
  );
}

function AvatarInitials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-2xs font-semibold text-primary">
      {initials || "?"}
    </span>
  );
}
